const article = {
	slug: "momentum-one-small-thing",
	title: "A task app with one button, for people who find starting harder than doing",
	date: "31 October 2026",
	updated: null,
	description:
		"Momentum is a free one-button web app for task initiation. Say who you are right now and how much energy you have, and it gives you one small action with a timer. No signup, no lists, no streaks.",
	keywords: [
		"ADHD",
		"task initiation",
		"procrastination",
		"executive function",
		"no streaks",
		"productivity app",
		"one button",
	],
	tldr: "Most task apps help you remember and organize. Momentum only helps with the first step. You say who you are right now and how much energy you have, press one button, and get one small action with a timer. It is free, has no signup, and has not been clinically tested.",
	readingTime: null,
	provenance: "Written for this site, October 2026.",
	provenanceUrl: null,
	series: null,
	artifact: null,
	hero: {
		src: "/articles-media/momentum-one-small-thing/hero.jpg",
		alt: "Three phone screens from Momentum: choosing banker and running low, a card that says to fix only the title slide, and a two minute timer.",
		caption: "Choose who you are right now, get one small action, start the timer.",
	},
	video: "https://momentum.swapniltamse.com/demo-1.mp4",
	markdown: `The report is due Friday. You have known that for nine days. The file is one click away, you have cleared the afternoon, and you are still looking at it.

Plenty of people know this moment. A lot of them have ADHD, and a lot of them have a drawer full of planners, a phone full of abandoned apps, and a long record of being told to just start.

Most task apps are built for a different problem. They help you capture, sort and remember. All of that happens before the hard moment and none of it touches it. A longer list can even make that moment worse, because now you also have to decide which item to face.

So I built [Momentum](https://momentum.swapniltamse.com), a web app that only works on that moment. It is free, it needs no signup, and it has one main button.

## How it works

You finish one sentence: "Right now I am a banker, and my energy is running low."

Then you press **Give me one thing**. The app shows one card with one action on it, a line about why that counts, and how long it should take. Most cards take between one and seven minutes.

If you say you are a banker with low energy, you might get this:

> Open the deck you keep avoiding. Fix only the title slide.
>
> The title slide counts as opening it.

You then have three choices. Start the timer, ask for another card, or say not now. When the timer runs out, it counted. The counter on the screen resets at midnight and nothing carries over.

![When you finish, the app says so and lets you leave.](/articles-media/momentum-one-small-thing/done.jpg)

## Who you are is only true for now

The public version used to offer only generic areas such as home, body and life admin. Generic advice is easy to ignore, though. "Work on your task for five minutes" could have been written for anyone, so it lands on no one.

Momentum now has 16 personas, including student, new mom, software engineer, job seeker, nurse, teacher, freelancer, caregiver, shift worker and retired, with 40 cards written for each. A job seeker can get this card:

> Open one listing. Copy the single line that sounds like you into a note.
>
> You did not have to apply. You only had to look.

A student can get this one:

> Open the syllabus for your hardest class. Find only the next due date.
>
> One date you can see is calmer than a whole semester of fog.

The picker does not create a profile, and that was a deliberate choice. A person can be a grad student at 10 AM, a parent at 4 PM and a freelancer after dinner. One tap changes it. If you do not want to pick anything, the sentence starts as "just me" and the button already works.

## What I left out

Every omission here is on purpose.

- **No streaks.** A streak turns one bad day into a visible loss. Momentum has no record of yesterday to lose.
- **No lists.** You see one card. If it is wrong, the next one is a tap away.
- **No failure screens.** Press "Not now" and the app says "That's okay. The button will be here." Stop a timer early and it says "Stopping is information, not failure."
- **No account.** Your progress stays in your browser, on your device.
- **No analytics or tracking code.** I do not know who you are or which cards you pressed.

Energy gets its own question because a wired brain and an exhausted one should not get the same task. Every persona has cards for a low-energy day, and a test in the code fails if one ever runs short.

There is also a small Aa button. It offers larger text, an easier font, and a focus mode that leaves only the sentence and the button on screen.

![Focus mode removes everything except the sentence and the button.](/articles-media/momentum-one-small-thing/focus.jpg)

Some cards touch hard feelings, so the app shows a crisis line at every step, in focus mode too.

## What the research says, and what it does not

I am an engineer, so I went looking for what is actually known, and the honest answer is that it is less tidy than the internet suggests.

Procrastination is often part of therapy for adults with ADHD, yet it is not an official ADHD symptom, and [one small 2014 study of 54 students](https://pubmed.ncbi.nlm.nih.gov/24992694/) called its own finding preliminary. It linked general procrastination to inattention specifically. [A 2019 study](https://pubmed.ncbi.nlm.nih.gov/30927231/) compared 29 adults with ADHD to 24 controls and found that the ADHD group carried out fewer of their own real-life intentions, and that this gap statistically accounted for part of the link between ADHD symptoms and procrastination.

Those are small studies, and neither one tested anything like Momentum. I read them as support for building something that acts in the moment of the intention, which is a design judgment on my part. Neither study proves that this app works.

Momentum has not been clinically tested. It is not a treatment and it does not replace a clinician, medication, therapy or a coach. It may simply not suit you, and that would say nothing about you.

The app has a [sources and limits page](https://momentum.swapniltamse.com/evidence) that goes through each design choice, what the research found, and where it falls short. One choice lacks support in the closest evidence, and I did not find research for two others. That page says which ones.

![Each design choice on the sources page names its evidence and its limit.](/articles-media/momentum-one-small-thing/evidence.jpg)

## How it was made

I built Momentum with Claude Code. I set the rules for the app and the cards: one action per card, no guilt, no medical or financial advice, nothing that puts a baby or a patient second, and no assumptions about your family or your money. Claude drafted the persona cards under those rules, and I decide what stays. Separate AI reviewers then read every card as hostile readers. They flagged 136 of the 640, including one that told a tired parent to hold a baby at an open window, and all of those were rewritten. A second check of the citations found errors of mine that are now fixed.

That means some of them will be wrong. A card written for a nurse without a nurse in the room will miss in ways I cannot see from here. If a card rings false for your life, tell me through [my site](https://swapniltamse.com/contact) and I will fix it. The same goes for a persona that is missing.

## Try it

[momentum.swapniltamse.com](https://momentum.swapniltamse.com) works on any phone or computer, and you can add it to your home screen. Pick nothing, press the button, and see what it hands you. If the card is wrong, the app will not mind.

## For journalists and editors

- **What it is:** A free web app that gives you one small action with a timer, chosen by who you are right now and how much energy you have.
- **Scale:** 16 personas and 913 written cards. No AI runs when you press the button.
- **Privacy:** No account, no ads and no analytics code. Progress stays on the device.
- **Evidence:** It has not been clinically tested. The [sources and limits page](https://momentum.swapniltamse.com/evidence) lists what is known and what is not.
- **Maker:** Swapnil Tamse, an engineering leader in New York.
- **Media:** [Demo video](https://momentum.swapniltamse.com/demo-1.mp4), [demo GIF](/articles-media/momentum-one-small-thing/demo.gif), [overview image](/articles-media/momentum-one-small-thing/hero.jpg), [card screenshot](/articles-media/momentum-one-small-thing/card.jpg) and [timer screenshot](/articles-media/momentum-one-small-thing/timer.jpg). You are welcome to use these when writing about Momentum.
- **Contact:** Reach me through [my contact page](https://swapniltamse.com/contact).`,
};

export default article;
